import React from 'react';
import {
  CameraIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import useSitePhotos from '../hooks/useSitePhotos';
import { formatStationFromFeet } from '../utils/filterSessionData';

const SitePhotosTab = ({ sessionId, onPhotoClick }) => {
  const { photos, loading, error } = useSitePhotos(sessionId);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading photos…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-900 font-medium">Couldn't load photos</p>
          <p className="text-sm text-gray-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <CameraIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-700 font-medium">No site photos yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Photos uploaded from the mobile app will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto px-4 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {photos.map((photo) => (
            <SitePhotoCard
              key={photo.id}
              photo={photo}
              onClick={() => onPhotoClick?.(photo)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SitePhotoCard = ({ photo, onClick }) => {
  const stationLabel =
    photo.station_ft != null ? formatStationFromFeet(photo.station_ft) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-red-500"
    >
      <div className="aspect-square bg-gray-100 relative">
        {photo.thumbnailUrl ? (
          <img
            src={photo.thumbnailUrl}
            alt={photo.description || photo.category || 'site photo'}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CameraIcon className="h-10 w-10 text-gray-300" />
          </div>
        )}
        {photo.category && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-white/90 text-gray-800 border border-gray-200">
            {photo.category}
          </span>
        )}
      </div>
      <div className="p-3 space-y-1">
        {stationLabel && (
          <div className="flex items-center text-xs text-gray-600">
            <MapPinIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
            <span className="truncate">{stationLabel}</span>
          </div>
        )}
        {photo.description && (
          <p className="text-sm text-gray-900 line-clamp-2">{photo.description}</p>
        )}
        {photo.taken_at && (
          <p className="text-xs text-gray-500">{formatTakenAt(photo.taken_at)}</p>
        )}
      </div>
    </button>
  );
};

function formatTakenAt(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default SitePhotosTab;
